/**
 * MKS-410 — the one "New request" form.
 *
 * NO PROJECT AND NO ASSIGNEE PICKER. That absence is the ticket, not an
 * omission: a reporter is not asked to know which team owns their problem, so
 * the form collects a type, a title, a description and attachments, and an
 * admin routes it afterwards. The backend refuses an over-posted `projectId`
 * or `assigneeId` with a 400, and `CreateHelpdeskRequestInput` has nowhere to
 * put one — three layers saying the same thing on purpose.
 *
 * ATTACHMENTS follow the portal's `NewRequestForm` (quick task 260809-ofp)
 * exactly, because the failure mode it solves is the same one here:
 *   - an oversized file is flagged client-side BEFORE any network call, so the
 *     byte cost is never paid for a doomed upload;
 *   - files upload sequentially AFTER the request is created, and each
 *     failure is named with its own server-reported reason;
 *   - a retry after a partial failure is guarded by `createdRequest`, so
 *     pressing the primary control again NEVER files a second request — it
 *     clears the form for the next one.
 */
import { useState, useCallback, type FormEvent } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  useCreateHelpdeskRequest,
  useHelpdeskRequestTypes,
  type AttachmentResult,
} from '../../hooks/useHelpdeskRequests';
import {
  MAX_ATTACHMENTS_PER_REQUEST,
  MAX_ATTACHMENT_BYTES,
  MAX_REQUEST_DESCRIPTION_LENGTH,
  MAX_REQUEST_TITLE_LENGTH,
  type HelpdeskRequestRecord,
} from '../../services/api/helpdeskRequests';

interface StagedFile {
  uid: number;
  file: File;
  /** Flagged client-side, before any network call, when the file is too big. */
  oversized: boolean;
  /** Set after a partial-failure response names this file's rejection reason. */
  errorMessage?: string;
  /** Set true once this file's upload is confirmed to have succeeded. */
  sent?: boolean;
}

let nextUid = 1;

export interface NewHelpdeskRequestFormProps {
  /** Fired once a request (and all of its attachments) has fully settled. */
  onCreated?: (request: HelpdeskRequestRecord) => void;
}

export function NewHelpdeskRequestForm({ onCreated }: NewHelpdeskRequestFormProps) {
  const [typeId, setTypeId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState(false);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [createdRequest, setCreatedRequest] = useState<HelpdeskRequestRecord | null>(null);
  const [hasFailures, setHasFailures] = useState(false);

  const { data: types = [], isLoading: typesLoading } = useHelpdeskRequestTypes();

  const mutation = useCreateHelpdeskRequest(
    (request, attachmentResults: AttachmentResult[]) => {
      setCreatedRequest(request);
      const failures = attachmentResults.filter((r) => r.status === 'error');
      if (failures.length === 0) {
        onCreated?.(request);
        return;
      }
      setHasFailures(true);
      setStaged((prev) =>
        prev.map((f) => {
          if (f.oversized) return f;
          const failure = failures.find((fr) => fr.filename === f.file.name);
          if (failure) {
            return { ...f, sent: false, errorMessage: failure.message ?? 'Upload failed' };
          }
          return { ...f, sent: true };
        }),
      );
    },
  );

  const onDrop = useCallback((accepted: File[]) => {
    setStaged((prev) => {
      const room = MAX_ATTACHMENTS_PER_REQUEST - prev.length;
      if (room <= 0) return prev;
      const slice = accepted.slice(0, room).map((file) => ({
        uid: nextUid++,
        file,
        oversized: file.size > MAX_ATTACHMENT_BYTES,
      }));
      return [...prev, ...slice];
    });
  }, []);

  // No `accept` filter: the backend takes any content type on this surface,
  // so the picker must offer every file type.
  const { getRootProps, getInputProps, isDragActive, open: openPicker } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  function removeFile(uid: number) {
    setStaged((prev) => prev.filter((f) => f.uid !== uid));
  }

  /** Back to an empty form, for filing the next request. */
  function resetForm() {
    setTypeId('');
    setTitle('');
    setDescription('');
    setTouched(false);
    setStaged([]);
    setCreatedRequest(null);
    setHasFailures(false);
    mutation.reset();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // The request was already created on a prior submit that hit a partial
    // attachment failure. Pressing the primary control again must NEVER create
    // a second one.
    if (createdRequest) {
      onCreated?.(createdRequest);
      resetForm();
      return;
    }
    setTouched(true);
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    if (!typeId || !trimmedTitle || !trimmedDescription || mutation.isPending) return;
    mutation.mutate({
      input: {
        typeId: Number(typeId),
        title: trimmedTitle,
        description: trimmedDescription,
      },
      files: staged.filter((f) => !f.oversized).map((f) => f.file),
    });
  }

  const submitError = mutation.isError
    ? ((mutation.error as Error)?.message ?? 'Could not submit. Try again.')
    : null;

  const locked = !!createdRequest;

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="new-request-form">
      {/* Type */}
      <div>
        <label
          htmlFor="helpdesk-request-type"
          className="block text-sm font-medium text-ms-text-strong mb-1"
        >
          What kind of request is it?
          <span className="text-red-600 ml-0.5">*</span>
        </label>
        <select
          id="helpdesk-request-type"
          value={typeId}
          disabled={locked || typesLoading}
          onChange={(e) => setTypeId(e.target.value)}
          className="w-full px-3 py-2 border border-ms-border rounded-lg text-ms-text focus:outline-none focus:ring-2 focus:ring-ms-orange focus:border-transparent disabled:opacity-60"
        >
          <option value="">
            {typesLoading ? 'Loading…' : 'Pick a type'}
          </option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        {touched && !typeId && !locked && (
          <p className="text-xs text-red-600 mt-1">Pick a request type</p>
        )}
      </div>

      {/* Title */}
      <div>
        <label
          htmlFor="helpdesk-request-title"
          className="block text-sm font-medium text-ms-text-strong mb-1"
        >
          Title
          <span className="text-red-600 ml-0.5">*</span>
        </label>
        <input
          id="helpdesk-request-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="Give it a brief, clear title"
          maxLength={MAX_REQUEST_TITLE_LENGTH}
          disabled={locked}
          className="w-full px-3 py-2 border border-ms-border rounded-lg text-ms-text focus:outline-none focus:ring-2 focus:ring-ms-orange focus:border-transparent placeholder:text-ms-text-muted disabled:opacity-60"
        />
        {touched && !title.trim() && !locked && (
          <p className="text-xs text-red-600 mt-1">Title is required</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="helpdesk-request-description"
          className="block text-sm font-medium text-ms-text-strong mb-1"
        >
          Describe it
          <span className="text-red-600 ml-0.5">*</span>
        </label>
        <textarea
          id="helpdesk-request-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="What happened, what you expected, anything that helps whoever picks this up"
          rows={6}
          maxLength={MAX_REQUEST_DESCRIPTION_LENGTH}
          disabled={locked}
          className="w-full px-3 py-2 border border-ms-border rounded-lg text-ms-text resize-none focus:outline-none focus:ring-2 focus:ring-ms-orange focus:border-transparent placeholder:text-ms-text-muted disabled:opacity-60"
        />
        {touched && !description.trim() && !locked && (
          <p className="text-xs text-red-600 mt-1">Description is required</p>
        )}
      </div>

      {/* Attachments */}
      <div>
        <p className="text-sm font-medium text-ms-text-strong mb-2">Attachments</p>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-default transition-colors ${
            isDragActive
              ? 'border-ms-orange bg-orange-50'
              : 'border-ms-border bg-ms-canvas hover:border-ms-text-muted'
          }`}
        >
          <input {...getInputProps({ 'aria-label': 'Attach files' })} />
          <p className="text-sm text-ms-text-secondary mb-2">
            Drag and drop files or click to browse
          </p>
          <button
            type="button"
            onClick={openPicker}
            disabled={locked}
            className="text-sm font-medium text-ms-orange hover:underline disabled:opacity-50"
          >
            Browse files
          </button>
          <p className="text-xs text-ms-text-muted mt-1">
            Any file type — 25 MB cap per file, {MAX_ATTACHMENTS_PER_REQUEST} files max
          </p>
        </div>

        {staged.length > 0 && (
          <ul className="mt-3 space-y-1">
            {staged.map(({ uid, file, oversized, errorMessage: fileError, sent }) => {
              const isError = oversized || !!fileError;
              return (
                <li
                  key={uid}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                    isError
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-ms-surface-2 text-ms-text'
                  }`}
                >
                  <span className="flex flex-col min-w-0">
                    <span className="truncate max-w-xs">{file.name}</span>
                    {oversized && (
                      <span className="text-xs text-red-600">
                        Exceeds the 25 MB per-file cap — not uploaded
                      </span>
                    )}
                    {fileError && !oversized && (
                      <span className="text-xs text-red-600">{fileError}</span>
                    )}
                    {sent && !isError && (
                      <span className="text-xs text-ms-text-muted">Sent</span>
                    )}
                  </span>
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => removeFile(uid)}
                      className="ml-4 text-ms-text-muted hover:text-ms-text flex-shrink-0"
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {hasFailures && createdRequest && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="alert"
        >
          <p className="font-medium">
            Your request was filed, but some attachments failed to upload.
          </p>
          <p className="mt-1">
            They are named above. The request itself is in &ldquo;My requests&rdquo; — add
            the files there, or continue without them.
          </p>
        </div>
      )}

      {submitError && (
        <p className="text-sm text-red-600" role="alert">
          {submitError}
        </p>
      )}

      <div className="flex items-center gap-4 pt-1">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="bg-ms-orange text-white font-medium px-6 py-2 rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
        >
          {mutation.isPending
            ? 'Submitting…'
            : createdRequest
              ? 'Continue'
              : 'Submit request'}
        </button>
        {!locked && (title || description || typeId || staged.length > 0) && (
          <button
            type="button"
            onClick={resetForm}
            className="text-ms-text-secondary hover:text-ms-text text-sm"
          >
            Clear
          </button>
        )}
      </div>
    </form>
  );
}
